import { useState, useMemo, useEffect } from "react"; // Import useMemo and useEffect
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Layout } from "@/components/Layout";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input"; // Import Input
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select"; // Import Select components
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { CreateInvoicingDto, invoicingApi } from "@/lib/api";
import { Invoicing, InvoicingDeliveryType } from "@/lib/types";
import { FileText, PlusCircle, Pencil, Filter, XCircle } from "lucide-react"; // Import Filter and XCircle icons
import { InvoicingFormModal } from "@/components/OrderInterface/InvoicingFormModal";
import { useToast } from "@/components/ui/use-toast";

const INVOICING_FILTERS_KEY = "invoicing_filters";

export function InvoicingList() {
  const [ isModalOpen, setIsModalOpen ] = useState( false );
  const [ editingInvoicing, setEditingInvoicing ] = useState<Invoicing | null>( null );
  const [ filterName, setFilterName ] = useState( () => {
    const saved = localStorage.getItem( INVOICING_FILTERS_KEY );
    if ( saved ) {
      try { return JSON.parse( saved ).filterName || ""; } catch ( e ) { return ""; }
    }
    return "";
  } );
  const [ filterDeliveryType, setFilterDeliveryType ] = useState<number | "all">( () => {
    const saved = localStorage.getItem( INVOICING_FILTERS_KEY );
    if ( saved ) {
      try { return JSON.parse( saved ).filterDeliveryType || "all"; } catch ( e ) { return "all"; }
    }
    return "all";
  } );
  const [ filterQuantity, setFilterQuantity ] = useState<string>( () => {
    const saved = localStorage.getItem( INVOICING_FILTERS_KEY );
    if ( saved ) {
      try { return JSON.parse( saved ).filterQuantity || ""; } catch ( e ) { return ""; }
    }
    return "";
  } );

  useEffect( () => {
    localStorage.setItem( INVOICING_FILTERS_KEY, JSON.stringify( {
      filterName,
      filterDeliveryType,
      filterQuantity
    } ) );
  }, [ filterName, filterDeliveryType, filterQuantity ] );

  const queryClient = useQueryClient();
  const { toast } = useToast();

  const {
    data: invoicing,
    isLoading,
  } = useQuery( {
    queryKey: [ "invoicing" ],
    queryFn: async () => {
      const response = await invoicingApi.getAll();
      return response?.data.sort( ( a, b ) => a.id - b.id ) || [];
    },
    staleTime: 0,
    gcTime: 0,
    refetchOnWindowFocus: true,
  } );

  const createMutation = useMutation( {
    mutationFn: ( newInvoicing: CreateInvoicingDto ) => invoicingApi.create( newInvoicing ),
    onSuccess: () => {
      queryClient.invalidateQueries( { queryKey: [ "invoicing" ] } );
      setIsModalOpen( false );
      setEditingInvoicing( null );
      toast( {
        title: "Sucesso!",
        description: "Faturamento criado com sucesso.",
      } );
    },
    onError: ( error ) => {
      toast( {
        title: "Erro",
        description: `Não foi possível criar o faturamento: ${ error.message }`,
        variant: "destructive",
      } );
    },
  } );

  const updateMutation = useMutation( {
    mutationFn: ( { id, data }: { id: number; data: CreateInvoicingDto } ) =>
      invoicingApi.update( id, data ),
    onSuccess: () => {
      queryClient.invalidateQueries( { queryKey: [ "invoicing" ] } );
      setIsModalOpen( false );
      setEditingInvoicing( null );
      toast( {
        title: "Sucesso!",
        description: "Faturamento atualizado com sucesso.",
      } );
    },
    onError: ( error ) => {
      toast( {
        title: "Erro",
        description: `Não foi possível atualizar o faturamento: ${ error.message }`,
        variant: "destructive",
      } );
    },
  } );

  const handleOpenCreateModal = () => {
    setEditingInvoicing( null );
    setIsModalOpen( true );
  };

  const handleOpenEditModal = ( item: Invoicing ) => {
    setEditingInvoicing( item );
    setIsModalOpen( true );
  };

  const handleFormSubmit = ( data: CreateInvoicingDto ) => {
    if ( editingInvoicing ) {
      updateMutation.mutate( { id: editingInvoicing.id, data } );
    } else {
      createMutation.mutate( data );
    }
  };

  const formatDate = ( dateString: string ) => {
    return new Date( dateString ).toLocaleDateString( "pt-BR", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    } );
  };

  const getDeliveryTypeName = ( id: number ) => {
    const name = InvoicingDeliveryType[ id ];
    return name || "Desconhecido";
  };

  const filteredInvoicing = useMemo( () => {
    if ( !invoicing ) return [];

    return invoicing.filter( ( item ) => {
      const matchesName = filterName
        ? item.invoicingName.toLowerCase().includes( filterName.toLowerCase() )
        : true;

      const matchesDeliveryType =
        filterDeliveryType === "all"
          ? true
          : item.deliveryTypeId === filterDeliveryType;

      const matchesQuantity = filterQuantity
        ? item.invoicingQtd === Number( filterQuantity )
        : true;

      return matchesName && matchesDeliveryType && matchesQuantity;
    } );
  }, [ invoicing, filterName, filterDeliveryType, filterQuantity ] );

  const handleClearFilters = () => {
    setFilterName( "" );
    setFilterDeliveryType( "all" );
    setFilterQuantity( "" );
  };

  return (
    <Layout title="Faturamentos">
      <div className="space-y-6">
        <Card>
          <CardHeader className="bg-gradient-to-r from-primary to-secondary text-white">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <FileText className="h-6 w-6" />
                <CardTitle className="text-xl">
                  Listagem de Faturamentos
                </CardTitle>
              </div>
              <Button
                onClick={handleOpenCreateModal}
                variant="outline"
                size="sm"
                className="bg-white text-primary hover:bg-gray-100"
              >
                <PlusCircle className="h-4 w-4 mr-2" />
                Adicionar
              </Button>
            </div>
          </CardHeader>
          <CardContent className="p-6">
            <div className="mb-4 flex flex-wrap items-end gap-4">
              <div className="flex-1 min-w-[180px]">
                <label htmlFor="filterName" className="text-sm font-medium text-gray-700">
                  Mês de referência
                </label>
                <Input
                  id="filterName"
                  placeholder="Filtrar por Mês de referencia"
                  value={filterName}
                  onChange={( e ) => setFilterName( e.target.value )}
                  className="mt-1"
                />
              </div>
              <div className="flex-1 min-w-[180px]">
                <label htmlFor="filterDeliveryType" className="text-sm font-medium text-gray-700">
                  Tipo de Entrega
                </label>
                <Select
                  value={String( filterDeliveryType )}
                  onValueChange={( value ) => setFilterDeliveryType( value === "all" ? "all" : Number( value ) )}
                >
                  <SelectTrigger className="mt-1">
                    <SelectValue placeholder="Todos" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Todos</SelectItem>
                    {Object.entries( InvoicingDeliveryType )
                      .filter( ( [ key, value ] ) => typeof value === "number" )
                      .map( ( [ key, value ] ) => (
                        <SelectItem key={value} value={String( value )}>
                          {key}
                        </SelectItem>
                      ) )}
                  </SelectContent>
                </Select>
              </div>
              <div className="flex-1 min-w-[180px]">
                <label htmlFor="filterQuantity" className="text-sm font-medium text-gray-700">
                  Quantidade
                </label>
                <Input
                  id="filterQuantity"
                  type="number"
                  placeholder="Filtrar por quantidade"
                  value={filterQuantity}
                  onChange={( e ) => setFilterQuantity( e.target.value )}
                  className="mt-1"
                />
              </div>
              <Button
                onClick={handleClearFilters}
                variant="outline"
                className="gap-2"
              >
                <XCircle className="h-4 w-4" />
                Limpar Filtros
              </Button>
            </div>

            {isLoading ? (
              <div className="text-center py-8">
                <div className="animate-pulse">Carregando faturamentos...</div>
              </div>
            ) : !filteredInvoicing || filteredInvoicing.length === 0 ? (
              <div className="text-center py-8">
                <FileText className="h-12 w-12 mx-auto text-gray-400 mb-4" />
                <p className="text-gray-600">Nenhum faturamento encontrado</p>
              </div>
            ) : (
              <div className="rounded-lg border overflow-hidden">
                <Table>
                  <TableHeader>
                    <TableRow className="bg-gray-50">
                      <TableHead>ID</TableHead>
                      <TableHead>Mês de referencia</TableHead>
                      <TableHead>Tipo de Entrega</TableHead>
                      <TableHead>Quantidade</TableHead>
                      <TableHead className="text-center">Data de Criação</TableHead>
                      <TableHead className="text-center">Ações</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {filteredInvoicing.map( ( item: Invoicing ) => (
                      <TableRow key={item.id} className="hover:bg-gray-50">
                        <TableCell className="font-medium">{item.id}</TableCell>
                        <TableCell>{item.invoicingName}</TableCell>
                        <TableCell>{getDeliveryTypeName( item.deliveryTypeId )}</TableCell>
                        <TableCell>{item.invoicingQtd}</TableCell>
                        <TableCell className="text-center text-sm text-gray-600">
                          {item.createdAt ? formatDate( item.createdAt ) : "-"}
                        </TableCell>
                        <TableCell className="text-center">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleOpenEditModal( item )}
                          >
                            <Pencil className="h-4 w-4" />
                            <span className="sr-only">Editar</span>
                          </Button>
                        </TableCell>
                      </TableRow>
                    ) )}
                  </TableBody>
                </Table>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
      <InvoicingFormModal
        isOpen={isModalOpen}
        onClose={() => {
          setIsModalOpen( false );
          setEditingInvoicing( null );
        }}
        onSubmit={handleFormSubmit}
        isLoading={createMutation.isPending || updateMutation.isPending}
        initialData={editingInvoicing || undefined}
      />
    </Layout>
  );
}